import { z } from 'zod';
import { zGoSlice } from '../helpers/fix-go-types';
import { RepresentativeSchema } from './representative';

const salesPeriodDays = [7, 14, 30, 60, 90, 180, 365] as const;
export type SalesPeriodDays = (typeof salesPeriodDays)[number];

export const thresholdWeeks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export type ThresholdWeeks = (typeof thresholdWeeks)[number];

export const reorderWeeks = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export type ReorderWeeks = (typeof reorderWeeks)[number];

// Dropdown options for the two week settings, shared by the order edit
// popover and the vendor defaults form. Both surfaces offer choices rather
// than a number input: these values round-trip through the API and back
// through the schemas above, and a typed-in 0 or 24 comes back rejected,
// which strands the order it was saved against.
const toWeekOptions = <T extends number>(weeks: readonly T[]) =>
  weeks.map((value) => ({ value, label: String(value) }));

export const thresholdWeekOptions = toWeekOptions(thresholdWeeks);
export const reorderWeekOptions = toWeekOptions(reorderWeeks);

// form validation rules
const OrderDefaultsSchema = z.object({
  qoh_threshold_weeks: z.literal(thresholdWeeks),
  reorder_weeks: z.literal(reorderWeeks),

  send_method: z.string(),

  // sales_period_days is not settable in any UI, but the suggestion math still
  // reads it and 33 vendors carry a non-30 value — see ORD-390. Declared so it
  // keeps its type; qoh_threshold_percent is deliberately absent, see the note
  // on OrderOptionsSchema.
  sales_period_days: z.literal(salesPeriodDays),
});

export const OrderSettingsSchema = z.object({
  defaults: OrderDefaultsSchema,

  // Neither carries omitempty on the Go side (models/vendors.go:85,89), so a
  // nil slice arrives as null against what used to be a bare required array —
  // dropping the whole settings object, and with it the whole vendor.
  send_methods: zGoSlice(z.string()),
  representatives: zGoSlice(RepresentativeSchema),
});

export type OrderSettings = z.infer<typeof OrderSettingsSchema>;
